import type { AppParams, Scope, WidgetUser } from '../types';
import { getEffectiveModuleId } from './feature-payload';

/**
 * Request payload for /activity/* endpoints, narrowed to the scope the
 * user clicked from in the home grid.
 *
 *   - scope='course'  → only course_remote_id (MOD)
 *   - scope='section' → course_remote_id + course_section_remote_ids (UDI)
 *   - scope='module'  → course_remote_id + course_section_remote_ids
 *                        + course_module_remote_ids (module_id)
 *
 * Sending only what the scope demands keeps the backend prompt focused
 * (e.g. a course-level summary shouldn't be biased by the current module).
 * The user object is always forwarded for telemetry/session resolution.
 */
export type ActivityContext = {
  course_remote_id: string;
  course_section_remote_ids?: string[];
  course_module_remote_ids?: string[];
  user: WidgetUser;
};

export function activityContextFromParams(
  params: AppParams,
  scope: Scope
): ActivityContext | null {
  if (!params.MOD) return null;
  const base = { course_remote_id: params.MOD, user: params.user };

  if (scope === 'course') {
    return base;
  }

  if (scope === 'section') {
    if (!params.UDI) return null;
    return { ...base, course_section_remote_ids: [params.UDI] };
  }

  // scope === 'module' — module_id can be explicit OR derived from route
  const moduleId = getEffectiveModuleId(params);
  if (!params.UDI || !moduleId) return null;
  return {
    ...base,
    course_section_remote_ids: [params.UDI],
    course_module_remote_ids: [moduleId]
  };
}
